import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Trophy, RefreshCw, BookOpen, TrendingUp, ChevronRight, Star } from 'lucide-react';
import { getScoreRating, MAX_SCORE } from '../data/scenarios';

const Confetti = () => {
  const colors = ['hsl(201 82% 55%)', 'hsl(174 55% 50%)', 'hsl(38 90% 55%)', 'hsl(0 0% 90%)', 'hsl(145 65% 50%)'];
  const particles = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    color: colors[Math.floor(Math.random() * colors.length)],
    delay: Math.random() * 2,
    duration: 2 + Math.random() * 2,
    size: 6 + Math.random() * 8,
  }));

  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden">
      {particles.map(p => (
        <div
          key={p.id}
          className="confetti-particle"
          style={{
            left: `${p.left}%`,
            top: '-20px',
            background: p.color,
            width: p.size,
            height: p.size,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
    </div>
  );
};

const ResultScreen = ({ score, onRestart, onShowProducts }) => {
  const rating = getScoreRating(score);
  const percentage = Math.max(0, Math.round((score / MAX_SCORE) * 100));
  const showConfetti = percentage >= 60;

  const tips = [
    'Всегда уточняйте возраст ребёнка — это определяет выбор продукта.',
    'Синомарин Беби — единственный подходящий вариант для детей до 5 лет.',
    'Синомарин Плюс с Algomer™ — лучший выбор при сухости и хроническом рините.',
    'Рассказывайте о пользе продукта в дороге и путешествиях — это повышает чек.',
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'hsl(var(--background))' }}>
      {showConfetti && <Confetti />}

      <div className="flex-1 px-5 py-10 max-w-xl mx-auto w-full">
        {/* Result card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="rounded-3xl overflow-hidden mb-6"
          style={{
            background: 'hsl(var(--card))',
            border: '2px solid hsl(var(--border))',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {/* Header gradient */}
          <div
            className="relative px-6 py-8 text-center"
            style={{ background: 'var(--gradient-hero)' }}
          >
            {/* Trophy icon */}
            <motion.div
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
              className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4"
              style={{
                background: 'hsl(0 0% 100% / 0.15)',
                border: '2px solid hsl(0 0% 100% / 0.3)',
                fontSize: 40,
              }}
            >
              {rating.emoji}
            </motion.div>

            <h2 className="text-2xl font-semibold mb-1" style={{ color: 'hsl(0 0% 100%)' }}>
              {rating.label}!
            </h2>
            <p className="text-sm" style={{ color: 'hsl(195 100% 88%)' }}>Симуляция завершена</p>
          </div>

          {/* Score display */}
          <div className="px-6 py-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium" style={{ color: 'hsl(var(--muted-foreground))' }}>Результат</span>
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-2xl font-semibold"
                style={{ color: 'hsl(var(--primary))' }}
              >
                {score} / {MAX_SCORE}
              </motion.span>
            </div>

            {/* Progress bar */}
            <div className="progress-track mb-1">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${percentage}%` }}
                transition={{ delay: 0.4, duration: 0.8, ease: 'easeOut' }}
                className="h-full rounded-full"
                style={{ background: 'var(--gradient-ocean)' }}
              />
            </div>
            <p className="text-xs text-right" style={{ color: 'hsl(var(--muted-foreground))' }}>{percentage}%</p>

            {/* Stars */}
            <div className="flex justify-center gap-2 mt-4">
              {[1, 2, 3].map(star => (
                <motion.div
                  key={star}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: percentage >= star * 33 ? 1 : 0.6, opacity: percentage >= star * 33 ? 1 : 0.3 }}
                  transition={{ delay: 0.5 + star * 0.15, type: 'spring' }}
                >
                  <Star
                    size={28}
                    fill={percentage >= star * 33 ? 'hsl(38 90% 55%)' : 'none'}
                    color={percentage >= star * 33 ? 'hsl(38 90% 55%)' : 'hsl(var(--muted-foreground))'}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Tips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="rounded-2xl p-5 mb-6"
          style={{
            background: 'hsl(var(--card))',
            border: '1.5px solid hsl(var(--border))',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          <div className="flex items-center gap-2 mb-4">
            <BookOpen size={16} color="hsl(var(--primary))" />
            <p className="text-sm font-semibold" style={{ color: 'hsl(var(--foreground))' }}>Ключевые советы</p>
          </div>
          <div className="flex flex-col gap-3">
            {tips.map((tip, i) => (
              <div key={i} className="flex items-start gap-3">
                <span
                  className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold"
                  style={{ background: 'hsl(var(--accent))', color: 'hsl(var(--accent-foreground))' }}
                >
                  {i + 1}
                </span>
                <p className="text-xs leading-relaxed" style={{ color: 'hsl(var(--foreground))' }}>{tip}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Action buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="flex flex-col gap-3"
        >
          <button
            onClick={onShowProducts}
            className="w-full flex items-center justify-between px-5 py-4 rounded-2xl text-sm font-semibold"
            style={{
              background: 'var(--gradient-ocean)',
              color: 'hsl(0 0% 100%)',
              boxShadow: 'var(--shadow-md)',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            <span className="flex items-center gap-2">
              <TrendingUp size={18} />
              Изучить линейку продуктов
            </span>
            <ChevronRight size={16} />
          </button>

          <button
            onClick={onRestart}
            className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl text-sm font-semibold"
            style={{
              background: 'hsl(var(--muted))',
              color: 'hsl(var(--foreground))',
              border: '1.5px solid hsl(var(--border))',
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'hsl(var(--accent))'}
            onMouseLeave={e => e.currentTarget.style.background = 'hsl(var(--muted))'}
          >
            <RefreshCw size={16} />
            Пройти ещё раз
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default ResultScreen;
